import clsx from 'clsx';
import React, { Ref, useEffect, useRef } from 'react';
import { DataAnalytics, DataAnalyticsClicks, DataAnalyticsTypes } from './analytics';
import { buildAnalyticsLocations } from './quickLink';
import useSplunkLogger from '../../common/hooks/useSplunkLogger';

export interface ActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  small?: boolean;
  isDisabled?: boolean;
  dataAnalytics?: DataAnalytics;
  ref?: Ref<HTMLButtonElement>;
  tabindex?: number;
  isModalOpen?: boolean;
  id?: string;
  dataTestId?: string;
}

const ActionButton = ({
  small,
  className,
  isDisabled,
  dataAnalytics,
  ref,
  tabindex,
  isModalOpen,
  id,
  dataTestId,
  ...props
}: ActionButtonProps): JSX.Element => {
  const {splunkLogger} = useSplunkLogger()
  const dataAnalyticsLocation = buildAnalyticsLocations(dataAnalytics?.locations,splunkLogger);
  const dataAnalyticsClick = dataAnalytics?.click
    ? dataAnalytics?.click
    : DataAnalyticsClicks?.NOT_AVAILABLE;
  const dataAnalyticsType = dataAnalytics?.type ? dataAnalytics.type : DataAnalyticsTypes?.Button;

  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isModalOpen && buttonRef.current) {
      const btn1 = document.querySelector<HTMLButtonElement>('[data-testid="AttentionDialogBtn1"]');
      const btn2 = document.querySelector<HTMLButtonElement>('[data-testid="AttentionDialogBtn2"]');

      if (btn1) {
        btn1.focus();
      } else if (btn2) {
        btn2.focus();
      }
    }
  }, [isModalOpen]);

  return (
    <button
      ref={buttonRef} 
      id={id}
      data-testid="actionButton"
      disabled={isDisabled}
      data-analytics-type={dataAnalyticsType}
      data-analytics-click={dataAnalyticsClick}
      data-analytics-location={dataAnalyticsLocation}
      tabIndex={tabindex}
      className={clsx(
        'w-full rounded-full flex items-center text-center justify-center py-sm',
        { 'text-base px-md': !small, 'text-xs px-sm': small },
        isDisabled
          ? 'bg-natural-500 bg-opacity-20 text-opacity-50 border-opacity-50 text-natural-600 border border-natural-600 cursor-not-allowed'
          : `${dataTestId? 'bg-btn-fill' : 'bg-secondary-100'} bg-opacity-10 text-navBlue hover:bg-secondary-300 hover:bg-opacity-100 hover:text-natural-100 border border-secondary-300`,
        className,
      )}
      {...props}
    />
  );
};

export default ActionButton;
